import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { makeStyles } from '@material-ui/core';
import MenuItemWithDialog from '../../../Shared/Table/widgets/TopBar/MenuItem/MenuItemWithDialog';
import RoundedButton from '../../../Shared/RoundedButton';
import { getEndpointChangeAlertAcknowledgeStatus } from '../../../../redux_store/reducer/reducers/alertsReducer/api';
import {
  enqueueSnackbar,
  SNACKBAR_VARIANTS,
} from '../../../../redux_store/reducer/reducers/notificationsReducer';
import CheckCircle from '@material-ui/icons/CheckCircleOutline';
import { getEndpoint } from '../../../../helpers/endpoint';
import useMetadataUpdate from '../../../../hooks/useMetadataUpdate';
import { useMutation } from 'react-fetching-library';
import showServerError from '../../../../helpers/showError';
import { NULL_QUERY_DELAY } from '../../../Shared/constants';
import {
  updateCanShowShimmer
} from '../../../../redux_store/reducer/reducers/tableReducer/tableReducer';

const useStyles = makeStyles((theme) => ({
  selectorMenu: {
    padding: 3,
    fontWeight: 400,
    maxWidth: 220,
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    overflow: 'hidden',
    fontSize: '1rem',
    boxSizing: 'border-box',
    lineHeight: 1.5,
    letterSpacing: '0.00938em',
  },
  icon: {
    width: 20,
    height: 20,
    fontSize: 16,
    color: 'black',
  },
  title: {
    color: 'black',
    marginLeft: 8,
  },
  driver: {
    borderTop: `1px solid ${theme.palette.text.secondary}`,
    opacity: 0.2,
  },
  description: {
    maxWidth: 200,
    paddingBottom: 12,
  },
  option: {
    justifyContent: 'flex-start',
  },
}));

const TableActions = ({
                        totalCount,
                        reloadData,
                        setAnchorElMulti,
                        filter,
                        sort,
                      }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const {
    en: {
      btn,
      tables: tTables,
      titles,
      errors,
      notifications,
    },
  } = useSelector((state) => state.i18n);
  const { metadata: { currentAccount } } = useSelector((state) => state.user);
  const [isOpenDialog, setOpenDialog] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isFailed, setIsFailed] = useState(false);

  const handleCloseDialog = () => {
    setAnchorElMulti(null);
    setOpenDialog(false);
  };

  const statusChangeAction = (endpoint, values) => {
    return {
      method: 'POST',
      endpoint: endpoint,
      body: values,
    };
  };

  const { mutate, loading: isStatusLoading } = useMetadataUpdate(useMutation, [statusChangeAction]);

  const onActionClick = async () => {
    let newEndpoint = getEndpoint(getEndpointChangeAlertAcknowledgeStatus(currentAccount.uuid), {}, sort, filter);
    const data = {
      'action': 'acknowledge',
      'totalCount': totalCount,
    };

    const { status, error, payload } = await mutate(newEndpoint, data);

    if (error) {
      switch (status) {
        case 422:
        case 500: {
          if (payload.validationErrors) {
            Object.keys(payload.validationErrors).forEach((key) =>
              showServerError(dispatch, payload.validationErrors[key]),
            );
            break;
          }
          showServerError(dispatch, errors.validationError);
          break;
        }
        default: {
          showServerError(dispatch, errors.unknownError);
          break;
        }
      }
      handleCloseDialog();
      setIsFailed(true);

      setTimeout(() => {
        setIsFailed(false);
      }, 3000);
    } else {
      setIsSuccess(true);
      dispatch(
        enqueueSnackbar({
          message: notifications.acknowledgeStatusChanged,
          options: {
            variant: SNACKBAR_VARIANTS.success,
          },
        }),
      );
      dispatch(updateCanShowShimmer(true));
      setTimeout(() => {
        reloadData({ delay: NULL_QUERY_DELAY });
        handleCloseDialog();
      }, 200);

      setTimeout(() => {
        setIsSuccess(false);
      }, 3000);
    }
  };

  const hasFilters = useMemo(
    () => !!filter.reduce((accumulator, { value }) => {
      return accumulator + value.toString().replaceAll(' ', '').length;
    }, 0),
    [filter],
  );

  return <MenuItemWithDialog
    icon={<CheckCircle className={classes.icon} />}
    title={<div className={classes.title}>
      {titles.acknowledge}
    </div>}
    onClick={() => {
      setOpenDialog(true);
    }}
    dialogTitle={tTables.changeStatus}
    dialogSubtitle={hasFilters ? tTables.miltiActionFilteredData : tTables.miltiActionAllData}
    dialogIsOpen={isOpenDialog}
    dialogHandleClose={handleCloseDialog}
    dialogOptions={[
      {
        label: tTables.status,
        value: btn.acknowledge,
      },
      {
        label: tTables.totalNumberOfSelected,
        value: totalCount,
      },
    ]}
    dialogActions={<RoundedButton
      onClick={onActionClick}
      isLoading={isStatusLoading}
      disabled={isStatusLoading}
      isSuccess={isSuccess}
      isFailed={isFailed}
      variant='contained'
      color='primary'
      size='small'
      className={classes.btn}
      LoadingWrapperProps={{
        LoaderProps: {
          disableShrink: true,
        },
      }}
    >
      {tTables.change}
    </RoundedButton>}
  />;
};

export default TableActions;
